import {BooleanInput, coerceBooleanProperty} from '@angular/cdk/coercion';
import {
  Directive,
  DoCheck,
  EventEmitter,
  forwardRef,
  Inject,
  inject, InjectionToken,
  Input,
  Output,
  Provider,
  Type
} from '@angular/core';
import {
  AbstractControl,
  AsyncValidator,
  ControlValueAccessor,
  NG_ASYNC_VALIDATORS,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  Validator,
  Validators,
} from '@angular/forms';
import {ApBaseClass, GenericControlValueAccessor} from 'webcommon/shared';

// These functions create Providers with normal defaults
// there may be cases where you would just create this separately for a different configuration of the Provider
// Also, these are only usable if you don't inject the NgControl in the component/directive,
// because if you provide this and inject NgControl, it would create a circular dependency.
export function DefaultValueAccessorProvider(val: Type<ControlValueAccessor>): Provider {
  return {
    multi: true,
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => val),
  };
}

export function DefaultValidatorProvider(val: Type<Validator>): Provider {
  return {
    multi: true,
    provide: NG_VALIDATORS,
    useExisting: forwardRef(() => val),
  };
}

export function DefaultAsyncValidatorProvider(val: Type<AsyncValidator>): Provider {
  return {
    multi: true,
    provide: NG_ASYNC_VALIDATORS,
    useExisting: forwardRef(() => val),
  };
}


export const ApCVAConfigToken = new InjectionToken<ApCVAConfig>('ApCVAConfig', {
  providedIn: 'root',
  factory() {
    return createDefaultCVAConfig();
  },
});
export function createDefaultCVAConfig(): ApCVAConfig {
  return {
    tryInjectNgControl: true,
  };
}

export interface ApCVAConfig {
  tryInjectNgControl: boolean;
}


// todo: convert non-generic stuff to mixin?
// like https://github.com/angular/components/blob/main/src/material/select/select.ts
// https://github.com/angular/components/blob/main/src/material/core/common-behaviors/disabled.ts

// This class is kinda based off of https://github.com/angular/angular/blob/main/packages/forms/src/directives/control_value_accessor.ts
// Inheritors of this class will be usable with ng-model, formControl, and binding to value directly
// But don't use more than 1 of these ways
// I based some logic off this as well:
// https://github.com/angular/components/blob/main/guides/creating-a-custom-form-field-control.md
// even though this is not actually a mat-form-field control
@Directive()
export abstract class ApBaseControlValueAccessor<TValueInput, TValueOutput = TValueInput>
extends ApBaseClass
implements GenericControlValueAccessor<TValueInput, TValueOutput>, DoCheck {
  // This is the AbstractControl attached to this Directive/Component
  // via [formControl] [formControlName] [ngModel]
  // This value can change over time, but it is expected to have an initial value during ngOnInit if NgControl is injected.
  // This is why the FormControlDirective.ngOnChanges() reacts to the potential change of the FormControl.
  protected get outerControl() {
    return this.ngControlDirective?.control;
  }

  private _previousOuterControl?: AbstractControl | null;

  // injects the Form control bound to the component.
  protected ngControlDirective: NgControl | null = this.config.tryInjectNgControl ?
    inject(NgControl, {
      self: true,
      optional: true,
    }) :
    null;

  /** Whether the control is required. */
  @Input()
  get required(): boolean {
    return this._required ?? this.outerControl?.hasValidator(Validators.required) ?? false;
  }
  set required(value: BooleanInput) {
    const newValue = coerceBooleanProperty(value);
    if (this._required !== newValue) {
      this._required = newValue;
    }
  }
  private _required: boolean | undefined;

  constructor(
    // The way this default value + @Inject() setup works
    // is that if there is a subclass explicitly calling this constructor,
    // then it will pass in a value or use the default defined here.
    // But if there is no constructor defined in the subclass,
    // then it will try to fill in this parameter via DI.
    // So either you make this parameter injectable, or you make this constructor protected,
    // and require an explicit constructor on all subclasses that will have to fill in this parameter.
    // And I didn't want to require a constructor everywhere, if possible.
    // This will probably change again at some point.
    @Inject(ApCVAConfigToken) private config: ApCVAConfig = createDefaultCVAConfig(),


    // inject stuff like this if any of it is necessary
    // in a subclass or here
    // These 2 have similar apis, but are for different ways of setting up forms:
    // parent ngForm for template driven forms
    //     @Optional() _parentForm: NgForm,
    // parent formGroup for reactive forms
    //     @Optional() _parentFormGroup: FormGroupDirective,
    // for example:
    // https://github.com/angular/components/blob/main/src/material/core/common-behaviors/error-state.ts
  ) {
    super();

    if (this.ngControlDirective) {
      // Setting the value accessor directly (instead of using
      // the NG_VALUE_ACCESSOR provider) to avoid running into a circular import.
      this.ngControlDirective.valueAccessor = this;
    }
  }

  ngDoCheck() {
    // track the outerControl, in case any setup needs to happen for a new one
    const outerControl = this.outerControl;
    if (outerControl !== this._previousOuterControl) {
      this._previousOuterControl = outerControl;
      if (outerControl) {
        this.onOuterControlChanged(outerControl);
      }
    }
  }

  /** `View -> model callback called when value changes` */
  protected _onChange = (_: TValueOutput) => {
    // will be replaced as part of ControlValueAccessor.registerOnChange
  }

  // Implemented as part of ControlValueAccessor.
  // Registers a callback function that is called when the control's value changes in the UI.
  registerOnChange(fn: (value: (TValueOutput)) => void): void {
    this._onChange = fn;
  }

  /** `View -> model callback called when control should be considered blurred or touched` */
  protected _onTouched = () => {
    // will be replaced as part of ControlValueAccessor.registerOnTouched
  }

  // Implemented as part of ControlValueAccessor.
  // Registers a callback function that is called by the forms API on initialization to update the form model on blur.
  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  // Implemented as part of ControlValueAccessor.
  // Writes a new value to the element.
  // This method is called by the forms API to write to the view when programmatic changes from model to view are requested.
  writeValue(obj: TValueInput): void {
    this.tryAssignValueProgrammatically(obj);
  }

  // Implemented as part of ControlValueAccessor.
  // Function that is called by the forms API when the control status changes to or from 'DISABLED'.
  // Depending on the status, it enables or disables the appropriate DOM element.
  setDisabledState(isDisabled: boolean): void {
    this.trySetDisabledProgrammatically(isDisabled);
  }

  /** Programmatically assigns a value to this control if it changed. */
  // tslint:disable-next-line:no-empty
  protected tryAssignValueProgrammatically(_newValue: TValueInput): void {
  }

  // propagate changes coming from view
  propagateChangesFromView(value: TValueOutput) {
    this._onChange(value);
  }

  // called when disabled is being set from framework
  // tslint:disable-next-line:no-empty
  protected trySetDisabledProgrammatically(_newValue: boolean): void {
  }

  // called when outerControl changes
  // tslint:disable-next-line:no-empty
  protected onOuterControlChanged(_outerControl: AbstractControl): void {
  }
}


// This class is only useful as an example for now if you're doing stuff outside of forms,
// Or using forms, but also trying to conform to some interface that expects to access the value, disabled, required, etc.
// Otherwise, you would just use formControl or ngModel to handle the value, and disabled, etc.
// tslint:disable-next-line:max-classes-per-file
@Directive()
export abstract class ApBaseControlValueAccessorWithProps<TValue>
extends ApBaseControlValueAccessor<TValue> {
  /** Value of the control. */
  @Input()
  get value(): TValue {
    return this._value;
  }
  set value(newValue: TValue) {
    this.tryAssignValueProgrammatically(newValue);
  }
  private _value: TValue;
  @Output() readonly valueChange = new EventEmitter<TValue>();

  /** Whether the control is disabled. */
  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(value: BooleanInput) {
    const newValue = coerceBooleanProperty(value);
    this.trySetDisabledProgrammatically(newValue);
  }
  private _disabled = false;

  protected override tryAssignValueProgrammatically(newValue: TValue): void {
    super.tryAssignValueProgrammatically(newValue);
    if (this._value !== newValue) {
      this.assignValueProgrammatically(newValue);
    }
  }

  protected assignValueProgrammatically(newValue: TValue): void {
    this._value = newValue;
  }

  override propagateChangesFromView(value: TValue) {
    this._value = value;
    this.valueChange.emit(value);
    super.propagateChangesFromView(value);
  }

  protected override trySetDisabledProgrammatically(newValue: boolean): void {
    super.trySetDisabledProgrammatically(newValue);
    if (this._disabled !== newValue) {
      this.setDisabledProgrammatically(newValue);
    }
  }

  protected setDisabledProgrammatically(newValue: boolean): void {
    this._disabled = newValue;
  }
}



// todo for tests:
// https://github.com/angular/angular/blob/9588b11e5b556fdc64e075e97d8f707f29cddcdf/packages/forms/test/reactive_integration_spec.ts#L3649

// todo: there was an issue that I now fixed:
// the status change issue is noticeable because when you make it dirty, then mark it back to pristine,
// it doesn't update the parents
// even though it appears to propagate the dirty

